import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../../../utils/createScopedI18n';
import { configServiceI18n } from './i18n';

const normalizeStatus = (value) => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();

  if (normalized === 'inactive' || normalized === 'inactivo') return 'inactive';
  return 'active';
};

export default function ServiceCreateEditModal({
  Modal,
  Input,
  Dropdown,
  show,
  mode = 'create',
  value,
  onChange,
  onClose,
  onSave,
  businessLineOptions = [],
  loadingBusinessLines = false,
}) {
  const { language } = useLanguage();
  const serviceLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: configServiceI18n });

  const isEdit = mode === 'edit';

  const [local, setLocal] = useState({
    id: null,
    name: '',
    businessLineId: null,
    businessLineName: '',
    status: 'active',
  });

  useEffect(() => {
    configServiceI18n.changeLanguage(serviceLanguage);
  }, [serviceLanguage]);

  useEffect(() => {
    if (show) {
      setLocal({
        id: value?.id ?? null,
        name: value?.name ?? '',
        businessLineId: value?.businessLineId ?? null,
        businessLineName: value?.businessLineName ?? '',
        status: normalizeStatus(value?.status),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, value?.id, value?.businessLineId, value?.businessLineName]);

  const nameValid = useMemo(() => local.name.trim().length > 0, [local.name]);
  const businessLineValid = useMemo(
    () => local.businessLineId != null && Number(local.businessLineId) > 0,
    [local.businessLineId]
  );

  const isValid =
    nameValid &&
    businessLineValid &&
    ['active', 'inactive'].includes(normalizeStatus(local.status));

  const setField = (key) => (eventOrValue) => {
    const nextValue = eventOrValue?.target
      ? eventOrValue.target.value
      : eventOrValue;
    setLocal((prev) => ({ ...prev, [key]: nextValue }));
  };

  const handleSave = () => {
    if (!isValid) return;
    const payload = {
      ...local,
      status: normalizeStatus(local.status),
    };
    onChange?.(payload);
    onSave?.(payload);
  };

  if (!Modal || !Input) return null;

  const statusOptions = [
    { label: t('config_service_status_active'), value: 'active' },
    { label: t('config_service_status_inactive'), value: 'inactive' },
  ];

  const statusOptionFrom = (currentValue) =>
    statusOptions.find((option) => option.value === normalizeStatus(currentValue)) ||
    statusOptions[0];

  const businessLineOptionFrom = (currentValue) => {
    if (currentValue == null) return null;
    const normalized = Number(currentValue);
    return (
      businessLineOptions.find((option) => Number(option.value) === normalized) ||
      null
    );
  };

  return (
    <Modal
      title={
        isEdit ? t('config_service_modal_title_edit') : t('config_service_modal_title_create')
      }
      show={show}
      onClose={onClose}
      primaryBtnTxt={
        isEdit ? t('config_service_modal_primary_edit') : t('config_service_modal_primary_create')
      }
      primaryBtnAction={handleSave}
      disabledPrimaryBtn={!isValid}
      secondaryBtnTxt={t('config_service_cancel')}
      secondaryBtnAction={onClose}
      ctaAligment='right'
      width='720px'
    >
      <div className='row g-3'>
        <div className='col-12'>
          <Input
            label={t('config_service_modal_name_label')}
            required
            placeholder={t('config_service_modal_name_placeholder')}
            value={local.name}
            onChange={setField('name')}
            error={local.name && !nameValid ? t('config_service_modal_name_required') : ''}
          />
        </div>

        <div className='col-12 col-md-6'>
          <label className='form-label'>
            {t('config_service_modal_business_line_label')} <span className='text-danger'>*</span>
          </label>
          {Dropdown ? (
            <Dropdown
              options={businessLineOptions}
              value={businessLineOptionFrom(local.businessLineId)}
              onChange={(option) => {
                setLocal((prev) => ({
                  ...prev,
                  businessLineId: option?.value || null,
                  businessLineName: option?.label || '',
                }));
              }}
              placeholder={
                loadingBusinessLines
                  ? t('config_service_modal_business_line_loading')
                  : t('config_service_modal_business_line_placeholder')
              }
              isDisabled={loadingBusinessLines}
              width='100%'
              menuPortalTarget={document.body}
              menuPosition='fixed'
            />
          ) : (
            <select
              className='form-select'
              value={local.businessLineId || ''}
              onChange={(event) => {
                const selectedId = parseInt(event.target.value, 10);
                const selectedOption = businessLineOptions.find(
                  (option) => option.value === selectedId
                );
                setLocal((prev) => ({
                  ...prev,
                  businessLineId: selectedId || null,
                  businessLineName: selectedOption?.label || '',
                }));
              }}
              disabled={loadingBusinessLines}
            >
              <option value=''>
                {loadingBusinessLines
                  ? t('config_service_modal_business_line_loading')
                  : t('config_service_modal_business_line_placeholder')}
              </option>
              {businessLineOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          )}
          {local.businessLineId && !businessLineValid && (
            <small className='text-danger'>
              {t('config_service_modal_business_line_required')}
            </small>
          )}
        </div>

        <div className='col-12 col-md-6'>
          <label className='form-label'>{t('config_service_modal_status_label')}</label>
          {Dropdown ? (
            <Dropdown
              options={statusOptions}
              value={statusOptionFrom(local.status)}
              onChange={(option) =>
                setLocal((prev) => ({
                  ...prev,
                  status: option?.value || 'active',
                }))
              }
              placeholder={t('config_service_modal_status_placeholder')}
              width='100%'
              menuPortalTarget={document.body}
              menuPosition='fixed'
            />
          ) : (
            <select
              className='form-select'
              value={local.status}
              onChange={setField('status')}
            >
              <option value='active'>{t('config_service_status_active')}</option>
              <option value='inactive'>{t('config_service_status_inactive')}</option>
            </select>
          )}
        </div>
      </div>
    </Modal>
  );
}
